/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Text, VStack } from "@chakra-ui/react";

// Components
import CustomModal from "../Modals/CustomModal";

// Utils
import { convertUnixTimestampToLocalDate } from "../../utils/helpers";

function formatFieldName(field: string): string {
	return field
		.replace(/\._\d+/g, "") // Remove array index markers like _0, _1
		.replace(/_/g, " ") // Replace underscores with spaces
		.replace(/\./g, " ") // Replace dots with spaces
		.replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
}

function formatValue(value: any): string {
	// Auto-detect Unix timestamps (seconds) and format them
	if (typeof value === "number" && value > 1000000000 && value < 9999999999) {
		return convertUnixTimestampToLocalDate(value).toLocaleString();
	}
	return JSON.stringify(value);
}

function simplifyDiff(diff: any, path = ""): any[] {
	if (!diff || typeof diff !== "object") return [];

	return Object.entries(diff).flatMap(([key, value]) => {
		if (key === "_t") return []; // Ignore metadata

		// Handle billing_summary specially - show submitted vs current totals
		if (key === "_billing_summary" && typeof value === "object") {
			return [
				{
					type: "billing_summary",
					field: "Billing Summary",
					submittedTotal: (value as any).submitted_total_network_cost,
					currentTotal: (value as any).current_total_network_cost
				}
			];
		}

		const fullPath = path ? `${path}.${key}` : key;
		const formattedField = formatFieldName(fullPath);

		// Handle modified values
		if (Array.isArray(value) && value.length === 2) {
			return [{ type: "modified", field: formattedField, oldValue: value[0], newValue: value[1] }];
		}

		// Handle removed values
		if (Array.isArray(value) && value.length === 3 && value[2] === 0) {
			if (typeof value[0] === "object" && value[0].file?.file_name) {
				// If removed item is a file, show only the file name
				return [{ type: "removed", field: formattedField, oldValue: `File: ${value[0].file.file_name}` }];
			}
			return [{ type: "removed", field: formattedField, oldValue: value[0] }];
		}

		if (typeof value === "object") {
			return simplifyDiff(value, fullPath);
		}

		return [];
	});
}

type RenderItem = { key: string | null; value: string | null; depth: number; isHeader: boolean };

function flattenJson(root: any): RenderItem[] {
	const items: RenderItem[] = [];
	const stack: { key: string | null; value: any; depth: number }[] = [{ key: null, value: root, depth: 0 }];

	while (stack.length > 0) {
		const { key, value, depth } = stack.pop()!;

		if (value === null || value === undefined) {
			// Handle null or undefined values
			items.push({ key, value: "—", depth, isHeader: false });
		} else if (typeof value !== "object") {
			// Handle primitive values
			items.push({ key, value: String(value), depth, isHeader: false });
		} else if (Array.isArray(value)) {
			// Handle arrays
			if (key !== null) items.push({ key, value: null, depth, isHeader: true });
			const nextDepth = key !== null ? depth + 1 : depth;
			// Push the elements onto the stack.
			for (let i = value.length - 1; i >= 0; i--) {
				stack.push({ key: null, value: value[i], depth: nextDepth });
			}
		} else {
			// Handle objects
			if (key !== null) items.push({ key, value: null, depth, isHeader: true });
			const nextDepth = key !== null ? depth + 1 : depth;
			const entries = Object.entries(value as object);
			// Push the elements onto the stack.
			for (let i = entries.length - 1; i >= 0; i--) {
				stack.push({ key: entries[i][0], value: entries[i][1], depth: nextDepth });
			}
		}
	}

	return items;
}

function FlatJsonView({ value }: { value: any }) {
	const items = flattenJson(value);
	return (
		<VStack align="start" spacing={0.5} width="100%">
			{items.map((item, i) => (
				<Box key={i} pl={`${item.depth * 12}px`} width="100%">
					{item.isHeader ? (
						<Text fontSize="sm" fontWeight="600">
							{formatFieldName(item.key!)}:
						</Text>
					) : item.key !== null ? (
						<Text fontSize="sm">
							<Text as="span" fontWeight="600">
								{formatFieldName(item.key)}:
							</Text>{" "}
							{item.value}
						</Text>
					) : (
						<Text fontSize="sm">{item.value}</Text>
					)}
				</Box>
			))}
		</VStack>
	);
}

const DiffModal = ({ isOpen, onClose, diff }: { isOpen: boolean; onClose: () => void; diff: any }) => {
	// If the diff is a simple string, display it
	if (typeof diff === "string") {
		return (
			<CustomModal
				title="Activity Details"
				isOpen={isOpen}
				onContinue={onClose}
				onCancel={onClose}
				cancelText={null}
				size="md"
				content={
					<Box bg="gray.100" p="4" borderRadius="md">
						<Text fontSize="md" fontStyle="italic" whiteSpace="pre-line">
							{diff}
						</Text>
					</Box>
				}
			/>
		);
	}

	// If the diff is a plain object (not a diff with old/new), render generically
	if (typeof diff === "object" && diff !== null && "_activityPayload" in diff) {
		const payload = diff._activityPayload;
		return (
			<CustomModal
				title="Activity Details"
				isOpen={isOpen}
				onContinue={onClose}
				onCancel={onClose}
				cancelText={null}
				size="md"
				content={
					<Box maxHeight="400px" overflowY="auto" bg="gray.100" p="4" borderRadius="md">
						<VStack align="start" spacing={3} width="100%">
							{Object.entries(payload).map(([key, value]) => (
								<Box key={key} p={3} bg="white" borderRadius="md" width="100%">
									<Text fontWeight="700" mb={1} fontSize="xs" textTransform="uppercase" color="gray.500">
										{formatFieldName(key)}
									</Text>
									<FlatJsonView value={value} />
								</Box>
							))}
						</VStack>
					</Box>
				}
			/>
		);
	}

	// If the diff is an object, process it normally
	const formattedDiff = simplifyDiff(diff);
	return (
		<CustomModal
			title="Changes Detected"
			isOpen={isOpen}
			onContinue={onClose}
			onCancel={onClose}
			cancelText={null}
			continueText="Done"
			size="xl"
			content={
				<Box maxHeight="400px" overflowY="auto" bg="white" p="4" borderRadius="md">
					{formattedDiff.length > 0 ? (
						<VStack align="start" spacing={3}>
							{formattedDiff.map((change, index) => (
								<Box
									key={index}
									p={2}
									borderRadius="md"
									width="100%"
									bg={
										change.type === "removed" ? "red.100" : change.type === "billing_summary" ? "blue.50" : "gray.100"
									}
									borderLeft={change.type === "billing_summary" ? "4px solid" : undefined}
									borderColor={change.type === "billing_summary" ? "blue.400" : undefined}>
									<Text fontWeight="500">{change.field}</Text>
									{change.type === "billing_summary" && (
										<>
											<Text color="gray.700">Original Submitted: ${change.submittedTotal}</Text>
											<Text color="gray.700">Current Total: ${change.currentTotal}</Text>
										</>
									)}
									{change.type === "modified" && (
										<>
											<Text color="red.500">Old: {formatValue(change.oldValue)}</Text>
											<Text color="green.500">New: {formatValue(change.newValue)}</Text>
										</>
									)}
									{change.type === "removed" && <Text color="red.500">Removed: {formatValue(change.oldValue)}</Text>}
								</Box>
							))}
						</VStack>
					) : (
						<Text>No changes detected.</Text>
					)}
				</Box>
			}
		/>
	);
};

export default DiffModal;
